import type { ReelFeedItem } from './types';

import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';
import { surfaceSide } from '~/composables/useHlsPlayer/startTarget';
import {
	getProxiedVideoUrl,
	prefetchFirstSegment
} from '~/composables/useVideoUrls';

import { clearIOSQualityCache, setIOSHighQualityUrl } from './iosQualityCache';
import { isReelCustomItem } from './types';

const HIGH_QUALITY_THRESHOLD_BPS = 4_000_000;

// On slow networks, only prefetch first segments of variants at or below this
// declared bandwidth (covers the new 540p rung at ~1.5 Mbps; excludes old
// single-variant originals at 4-9 Mbps).
const PREFETCH_MAX_SEGMENT_BPS_ON_SLOW = 2_500_000;

// User enabled Data Saver — segment prefetch would spend data the user asked
// us not to. Manifests (~400 B) are still allowed; segments (0.5-2.5 MB) are not.
function isDataSaverEnabled(): boolean {
	const connection = (
		navigator as Navigator & { connection?: { saveData?: boolean } }
	).connection;
	return connection?.saveData === true;
}

// On slow networks, delay prefetch so the current video has time to buffer
// before we consume bandwidth for upcoming videos.
function getPrefetchDelay(): number {
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= HIGH_QUALITY_THRESHOLD_BPS) return 0; // fast / unknown: immediate
	if (bps >= 2_000_000) return 1_000; // medium (~2-4 Mbps): 1s
	return 2_500; // slow (<2 Mbps): 2.5s
}

function getPrefetchDepth(): number {
	const bps = getSavedBandwidthEstimate();
	if (bps === null) return 2;
	if (bps >= 8_000_000) return 4;
	if (bps >= HIGH_QUALITY_THRESHOLD_BPS) return 3;
	if (bps >= 2_000_000) return 2;
	return 1;
}

interface UseShortsPrefetchOptions {
	getItems: () => ReelFeedItem[];
}

export function useShortsPrefetch(options: UseShortsPrefetchOptions) {
	const prefetchedUrls = new Set<string>();
	const inFlightUrls = new Set<string>();
	let prefetchAbortController: AbortController | null = null;
	let prefetchDelayTimer: ReturnType<typeof setTimeout> | null = null;

	function prefetchUpcoming(startIndex: number, count?: number) {
		if (import.meta.server) return;

		const effectiveCount = count ?? getPrefetchDepth();

		if (prefetchAbortController) {
			prefetchAbortController.abort();
		}

		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}

		prefetchAbortController = new AbortController();
		const { signal } = prefetchAbortController;

		const delay = getPrefetchDelay();
		const run = () => {
			prefetchDelayTimer = null;
			if (signal.aborted) return;
			// Hidden tab: the user is elsewhere — don't spend their bandwidth.
			// The next index change (they came back and swiped) re-triggers prefetch.
			if (document.visibilityState === 'hidden') return;
			const items = options.getItems();
			runPrefetch(items, startIndex, effectiveCount, signal);
		};

		if (delay > 0) prefetchDelayTimer = setTimeout(run, delay);
		else run();
	}

	function runPrefetch(
		items: ReelFeedItem[],
		startIndex: number,
		count: number,
		signal: AbortSignal
	) {
		for (let i = 1; i <= count; i++) {
			const nextIndex = startIndex + i;
			if (nextIndex >= items.length) break;

			const item = items[nextIndex];
			if (!item || item.kind !== 'video') continue;

			const videoUrl = getProxiedVideoUrl(item.videoUrl);
			if (
				!videoUrl ||
				prefetchedUrls.has(videoUrl) ||
				inFlightUrls.has(videoUrl)
			)
				continue;

			prefetchedUrls.add(videoUrl);
			inFlightUrls.add(videoUrl);

			const onSuccess = () => {
				inFlightUrls.delete(videoUrl);
			};
			const onError = (error: Error) => {
				inFlightUrls.delete(videoUrl);
				prefetchedUrls.delete(videoUrl);
			};

			if (i <= 1 || isDataSaverEnabled()) {
				// Adjacent video: manifest warm only (its preload player buffers the
				// rest). Data Saver: cap ALL prefetch at the manifest (~400 B) — the
				// user asked us not to spend their data on segments they may not watch.
				fetch(videoUrl, { priority: 'low', signal } as RequestInit)
					.then(onSuccess)
					.catch(onError);
			} else {
				const savedBps = getSavedBandwidthEstimate();
				const networkIsGood =
					savedBps !== null && savedBps >= HIGH_QUALITY_THRESHOLD_BPS;

				if (networkIsGood) {
					prefetchFirstSegment(videoUrl, signal, 'highest')
						.then(variantUrl => {
							if (variantUrl) setIOSHighQualityUrl(videoUrl, variantUrl);
							onSuccess();
						})
						.catch(onError);
				} else {
					// Low quality first guarantees something is ready. The bps cap
					// protects slow networks from old single-variant videos whose
					// "lowest" rung is a 4-9 Mbps original — a 4+ MB segment that
					// would hog the pipe for 30s+ competing with the active video
					// (manifests still warm either way).
					const lowQuality = prefetchFirstSegment(
						videoUrl,
						signal,
						'lowest',
						PREFETCH_MAX_SEGMENT_BPS_ON_SLOW,
						undefined,
						false,
						surfaceSide('feed')
					);

					lowQuality.then(onSuccess).catch(onError);

					// High quality is attempted opportunistically, but only on medium
					// networks — on genuinely slow ones it's guaranteed pipe hogging.
					const savedBpsForHigh = getSavedBandwidthEstimate();
					if (savedBpsForHigh !== null && savedBpsForHigh >= 2_000_000) {
						lowQuality
							.then(() => prefetchFirstSegment(videoUrl, signal, 'highest'))
							.then(variantUrl => {
								if (variantUrl) setIOSHighQualityUrl(videoUrl, variantUrl);
							})
							.catch(() => {});
					}
				}
			}
		}
	}

	function cleanupPrefetch() {
		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}
		prefetchAbortController?.abort();
		prefetchAbortController = null;
		prefetchedUrls.clear();
		inFlightUrls.clear();
		clearIOSQualityCache();
	}

	function abortCurrentPrefetch() {
		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}
		prefetchAbortController?.abort();
		prefetchAbortController = null;
	}

	return {
		prefetchUpcoming,
		abortCurrentPrefetch,
		cleanupPrefetch
	};
}
